import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LogIn, Plus, Wand2 } from 'lucide-react';
import { api, getStoredToken, saveSession } from '../../api/client';
import type { KorvexGame, SessionUser } from '../../types';

const USERNAME_RULE =
  '3–24 characters. Letters, numbers, underscores, and hyphens only. No , . \' @ # ~ ] } { [ = +';

export function EmbedStudioPage() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [games, setGames] = useState<KorvexGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [newGameOpen, setNewGameOpen] = useState(false);
  const [newGameName, setNewGameName] = useState('');

  useEffect(() => {
    document.documentElement.classList.add('dark');
    void loadSession();
  }, []);

  async function loadSession() {
    try {
      const { user: me } = await api.me();
      setUser(me);
      const { games: list } = await api.getMyGames();
      setGames(list);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    window.korvexAuthToken = () => getStoredToken();
    window.korvexOpenStudioGame = (gameId: string) => {
      window.dispatchEvent(new CustomEvent('korvex:studio-open', { detail: { gameId } }));
    };
    return () => {
      delete window.korvexOpenStudioGame;
      delete window.korvexAuthToken;
    };
  }, []);

  async function handleAuth(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const result =
        mode === 'login'
          ? await api.login(username.trim(), password)
          : await api.register(username.trim(), password);
      saveSession(result.token);
      setUser(result.user);
      const { games: list } = await api.getMyGames();
      setGames(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed.');
    }
  }

  async function createGame() {
    const name = newGameName.trim() || 'Untitled';
    try {
      const { game } = await api.createGame(name);
      setGames((prev) => [game, ...prev]);
      setNewGameOpen(false);
      setNewGameName('');
      openGame(game.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create game.');
    }
  }

  function openGame(gameId: string) {
    window.korvexOpenStudioGame?.(gameId);
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-korvex-bg">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-korvex-accent border-t-transparent" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-korvex-bg p-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="panel-card w-full max-w-md">
          <div className="mb-6 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-korvex-violet to-korvex-pink">
              <Wand2 className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold">Korvex Studio</h1>
              <p className="text-sm text-korvex-muted">Sign in to create games</p>
            </div>
          </div>
          <form onSubmit={handleAuth} className="space-y-4">
            <input className="input-field" placeholder="Username" value={username} onChange={(e) => setUsername(e.target.value)} required />
            <input type="password" className="input-field" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
            {mode === 'register' && <p className="text-xs text-korvex-muted">{USERNAME_RULE}</p>}
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button type="submit" className="btn-primary w-full">
              <LogIn className="h-4 w-4" />
              {mode === 'login' ? 'Sign in' : 'Create account'}
            </button>
          </form>
          <p className="mt-4 text-center text-sm text-korvex-muted">
            {mode === 'login' ? 'New here?' : 'Have an account?'}{' '}
            <button type="button" className="text-korvex-accent hover:underline" onClick={() => setMode(mode === 'login' ? 'register' : 'login')}>
              {mode === 'login' ? 'Register' : 'Sign in'}
            </button>
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-korvex-bg p-6">
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold">Your games</h1>
            <p className="text-korvex-muted">{user.username}</p>
          </div>
          <button type="button" className="btn-primary" onClick={() => setNewGameOpen(true)}>
            <Plus className="h-4 w-4" />
            New game
          </button>
        </div>

        {games.length === 0 ? (
          <div className="panel-card text-center text-korvex-muted">No games yet. Create your first one!</div>
        ) : (
          <div className="space-y-3">
            {games.map((game) => (
              <button
                key={game.id}
                type="button"
                onClick={() => openGame(game.id)}
                className="panel-card flex w-full items-center justify-between text-left transition hover:scale-[1.01] hover:shadow-glow-violet"
              >
                <div>
                  <p className="font-display text-lg font-semibold">{game.name}</p>
                  <p className="text-sm text-korvex-muted">{game.description}</p>
                  <div className="mt-2 flex gap-2">
                    <span className="rounded-full bg-white/5 px-2 py-0.5 text-xs">
                      {game.published ? 'Published' : 'Draft'}
                    </span>
                    {game.isPrivate && (
                      <span className="rounded-full bg-korvex-violet/20 px-2 py-0.5 text-xs text-korvex-violet">Private</span>
                    )}
                  </div>
                </div>
                <span className="text-korvex-accent">Open →</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {newGameOpen && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="panel-card w-full max-w-md space-y-4">
              <h2 className="font-display text-xl font-bold">New game</h2>
              <input className="input-field" placeholder="Game name" value={newGameName} onChange={(e) => setNewGameName(e.target.value)} autoFocus />
              <p className="text-xs text-korvex-muted">New games start private with description &quot;example&quot;.</p>
              <div className="flex gap-2">
                <button type="button" className="btn-ghost flex-1" onClick={() => setNewGameOpen(false)}>Cancel</button>
                <button type="button" className="btn-primary flex-1" onClick={() => void createGame()}>Create</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
